import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, CardHeader, Nav, NavItem, NavLink, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledPopover, PopoverBody } from 'reactstrap';
import classnames from 'classnames';
import TableContainer from '../../../components/TableContainerReactTable';
import { getRelatorio, getReportByDay } from '../../../api/reportApi';
import { getCampaign } from '../../../api/campaignApi';
import { Campaign } from '../../../types/campaign';
import { AdType } from '../../../types/order';
import numeral from 'numeral';
import 'numeral/locales/pt-br';
import { format, parse } from 'date-fns';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';

interface DeliveryReportTabsProps {
  campaignId: string;
}

export interface DeliveryReportTabsRef {
  refresh: () => Promise<void>;
}

const DeliveryReportTabs = forwardRef<DeliveryReportTabsRef, DeliveryReportTabsProps>(
  ({ campaignId }, ref) => {
    const [adReport, setAdReport] = useState<any[]>([]);
    const [adReportByDay, setAdReportByDay] = useState<any[]>([]);
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reportActiveTab, setReportActiveTab] = useState('1');
    const [columnByPublisherDropdownOpen, setColumnByPublisherDropdownOpen] = useState(false);
    const [columnByDayDropdownOpen, setColumnByDayDropdownOpen] = useState(false);
    const [visibleColumnsByPublisher, setVisibleColumnsByPublisher] = useState<number[]>([]);
    const [visibleColumnsByDay, setVisibleColumnsByDay] = useState<number[]>([]);
    // Helper function to check if campaign has audio or video creatives
    const hasAudioOrVideoCreatives = useCallback(() => {
      if (!campaign?.orders) return false;
      return campaign.orders.some(order => 
        order.adType === AdType.AUDIO || order.adType === AdType.VIDEO
      );
    }, [campaign]);

    // Column definitions for publisher report
    const columnsByPublisher = useMemo(() => [
      {
        id: 'publisher',
        header: 'Publisher',
        accessorFn: (row: any) => {
          // Parent row: show publisher name
          if (row.publisher) {
            return row.publisher.name;
          }
          // Child row: show order info (sizes, segmentation, creative line)
          if (row.order) {
            return `${row.order.adType} - ${row.order.sizes}`;
          }
          return '';
        },
        cell: (props: any) => {
          const row = props.row;
          const parentRow = row.getParentRow();console.log(row.original);
          const publisherId = row.original.publisher?.id || parentRow.original.publisher?.id;
          const orderId = row.original.order?.id;
          let recordSpanId = `record-${publisherId}`;
          if (orderId) {
            recordSpanId += `-${orderId}`;
          }
          return (
            <div
              style={{
                // Since rows are flattened by default,
                // we can use the row.depth property
                // and paddingLeft to visually indicate the depth
                // of the row
                paddingLeft: `${row.depth * 2}rem`,
              }}
            >
              <div>
                {row.getCanExpand() && (
                  <button className="btn btn-icon"
                          onClick={row.getToggleExpandedHandler()}>
                    {row.getIsExpanded() ? <i className="ri-arrow-down-line"></i> :
                      <i className="ri-arrow-right-line"></i>}
                  </button>
                )}
                {row.depth > 0 && (
                  <button className="btn btn-icon" style={{ cursor: 'default' }}>- </button>)}
                <span id={recordSpanId}>{props.getValue()}</span>
                {row.depth > 0 && (<>
                  <sup> <i className="ri-information-line"></i></sup>
                  <UncontrolledPopover placement="top"
                                       target={recordSpanId}
                                       trigger="hover">
                    <PopoverBody>
                      <p><strong>Compra:</strong> {row.original.order?.pricingModel}</p>
                      <p><strong>Tipo:</strong> {row.original.order?.adType}</p>
                      <p><strong>Bloco de anúncio:</strong> {row.original.order?.placement}</p>
                      <p><strong>Tamanhos:</strong> {row.original.order?.sizes}</p>
                      <p><strong>Segmentação:</strong> {row.original.order?.segmentation}</p>
                      <p className="mb-0"><strong>Linha criativa:</strong> {row.original.order?.creativeLine}</p>
                    </PopoverBody>
                  </UncontrolledPopover>
                </>)}
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'purchaseImpressions',
        header: 'Contratadas',
        accessorFn: (row: any) => row.purchaseImpressions || '-',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'impression',
        header: 'Entregues',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.impression || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'remaining',
        header: 'Restantes',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => {
          const pricingModel = row.order?.pricingModel;
          const contracted = row.purchaseImpressions || 0;
          
          if (pricingModel === 'CPM') {
            return contracted - (row.total.impression || 0);
          } else if (pricingModel === 'CPV') {
            return contracted - (row.total.viewable || 0);
          } else if (pricingModel === 'CPC') {
            return contracted - (row.total.click || 0);
          }
          return contracted - (row.total.impression || 0);
        },
        enableColumnFilter: false,
        enableSorting: true,
        sortDescFirst: true,
      },
      {
        id: 'viewable',
        header: 'Visíveis',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.viewable || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'blocked',
        header: 'Bloqueadas',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.blocked || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'click',
        header: 'Cliques',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.click || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'ctr',
        header: 'CTR',
        accessorFn: (row: any) => (row.total.click / row.total.impression) * 100 || 0,
        cell: (row: any) => `${numeral(row.getValue()).format('0.00')}%`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'viewability',
        header: 'Visibilidade',
        cell: (row: any) => {
          const viewability = row.getValue();
          const targetViewability = row.original?.order?.viewability || 0;
          const isBelowTarget = viewability < targetViewability;
          
          return (
            <div>
              <span className={`${isBelowTarget ? 'text-danger' : ''}`}>{numeral(viewability).format('0.00')}%</span>
              {isBelowTarget && (
                <i className="ri-alert-line ms-2 text-danger" title={`Meta: ${targetViewability}%`}></i>
              )}
            </div>
          );
        },
        accessorFn: (row: any) => (row.total.viewable / row.total.impression * 100) || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'delivery',
        header: 'Entrega',
        cell: (row: any) => `${numeral(row.getValue()).format('0.00')}%`,
        accessorFn: (row: any) => {
          return row.total.impression / row.purchaseImpressions * 100;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ], []);

    // Column definitions for day report
    const columnsByDay = useMemo(() => [
      {
        id: 'date',
        header: 'Data',
        accessorFn: (row: any) => {
          // Parent row: show date
          if (row.date) {
            return row.date;
          }
          // Child row: show publisher name and order info
          if (row.order && row.publisher) {
            return `${row.publisher.name} - ${row.order.adType} - ${row.order.tamanho}`;
          }
          return '';
        },
        cell: (props: any) => {
          const row = props.row;
          const parentRow = row.getParentRow();
          const date = row.original.date || (parentRow ? parentRow.original.date : null);
          const orderId = row.original.order?.id;
          const recordSpanId = `record-day-${date}-${orderId}`;
          // Parse as local date to avoid timezone shifts
          const rawDate = props.getValue();
          let displayDate = rawDate;
          if (typeof rawDate === 'string' && rawDate.includes('-')) {
            try {
              const parsed = parse(rawDate.split('T')[0], 'yyyy-MM-dd', new Date());
              if (!isNaN(parsed.getTime())) {
                displayDate = format(parsed, 'dd/MM/yyyy');
              }
            } catch (e) {
              displayDate = rawDate;
            }
          }
          return (
            <div
              style={{
                // Since rows are flattened by default,
                // we can use the row.depth property
                // and paddingLeft to visually indicate the depth
                // of the row
                paddingLeft: `${row.depth * 2}rem`,
              }}
            >
              <div>
                {row.getCanExpand() && (
                  <button className="btn btn-icon"
                          onClick={row.getToggleExpandedHandler()}>
                    {row.getIsExpanded() ? <i className="ri-arrow-down-line"></i> :
                      <i className="ri-arrow-right-line"></i>}
                  </button>
                )}
                {row.depth === 0 ? (
                  <span id={recordSpanId}>{displayDate}</span>) : (<>
                  <button className="btn btn-icon" style={{ cursor: 'default' }}>-</button>
                  <span id={recordSpanId}>{props.getValue()}</span>
                  <sup> <i className="ri-information-line"></i></sup>
                  <UncontrolledPopover placement="top"
                                       target={recordSpanId}
                                       trigger="hover">
                    <PopoverBody>
                      <p><strong>Compra:</strong> {row.original.order?.pricingModel}</p>
                      <p><strong>Tipo:</strong> {row.original.order?.adType}</p>
                      <p><strong>Bloco de anúncio:</strong> {row.original.order?.placement}</p>
                      <p><strong>Tamanhos:</strong> {row.original.order?.sizes}</p>
                      <p><strong>Segmentação:</strong> {row.original.order?.segmentation}</p>
                      <p className="mb-0"><strong>Linha criativa:</strong> {row.original.order?.creativeLine}</p>
                    </PopoverBody>
                  </UncontrolledPopover>
                </>)}
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
        sortDescFirst: true,
      },
      {
        id: 'purchaseImpressions',
        header: 'Contratadas',
        accessorFn: (row: any) => row.purchaseImpressions,
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'impression',
        header: 'Entregues',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.impression || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'remaining',
        header: 'Restantes',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => {
          const pricingModel = row.order?.pricingModel;
          const contracted = row.purchaseImpressions || 0;
          
          if (pricingModel === 'CPM') {
            return contracted - (row.total.impression || 0);
          } else if (pricingModel === 'CPV') {
            return contracted - (row.total.viewable || 0);
          } else if (pricingModel === 'CPC') {
            return contracted - (row.total.click || 0);
          }
          return contracted - (row.total.impression || 0);
        },
        enableColumnFilter: false,
        enableSorting: true,
        sortDescFirst: true,
      },
      {
        id: 'viewable',
        header: 'Visíveis',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.viewable || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'blocked',
        header: 'Bloqueadas',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.blocked || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'click',
        header: 'Cliques',
        cell: (row: any) => numeral(row.getValue()).format('0,0'),
        accessorFn: (row: any) => row.total.click || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'ctr',
        header: 'CTR',
        accessorFn: (row: any) => (row.total.click / row.total.impression) * 100 || 0,
        cell: (row: any) => `${numeral(row.getValue()).format('0.00')}%`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'viewability',
        header: 'Visibilidade',
        cell: (row: any) => {
          const viewability = row.getValue();
          const targetViewability = row.original?.order?.viewability || 0;
          const isBelowTarget = viewability < targetViewability;
          
          return (
            <div>
              <span className={`${isBelowTarget ? 'text-danger' : ''}`}>{numeral(viewability).format('0.00')}%</span>
              {isBelowTarget && (
                <i className="ri-alert-line ms-2 text-danger" title={`Meta: ${targetViewability}%`}></i>
              )}
            </div>
          );
        },
        accessorFn: (row: any) => (row.total.viewable / row.total.impression * 100) || 0,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'delivery',
        header: 'Entrega',
        cell: (row: any) => `${numeral(row.getValue()).format('0.00')}%`,
        accessorFn: (row: any) => {
          return row.total.impression / row.order.impressoes * 100;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ], []);

    // Initialize visible columns
    useEffect(() => {
      setVisibleColumnsByPublisher(columnsByPublisher.map((_, idx) => idx));
      setVisibleColumnsByDay(columnsByDay.map((_, idx) => idx));
    }, [columnsByPublisher, columnsByDay]);

    // Fetch data
    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [adReportData, adReportByDayData, campaignData] = await Promise.all([
          getRelatorio(parseInt(campaignId)),
          getReportByDay(parseInt(campaignId)),
          getCampaign(parseInt(campaignId))
        ]);

        setAdReport(adReportData);
        setAdReportByDay(adReportByDayData);
        setCampaign(campaignData);
      } catch (err) {
        console.error('Error fetching delivery report data:', err);
        setError('Erro ao carregar dados de entrega');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    const toggleColumnByPublisherDropdown = () => setColumnByPublisherDropdownOpen(!columnByPublisherDropdownOpen);
    const toggleColumnByDayDropdown = () => setColumnByDayDropdownOpen(!columnByDayDropdownOpen);

    const handleColumnByPublisherToggle = (idx: number) => {
      setVisibleColumnsByPublisher(prev => 
        prev.includes(idx) 
          ? prev.filter(i => i !== idx)
          : [...prev, idx]
      );
    };

    const handleColumnByDayToggle = (idx: number) => {
      setVisibleColumnsByDay(prev => 
        prev.includes(idx) 
          ? prev.filter(i => i !== idx)
          : [...prev, idx]
      );
    };

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardBody className="text-center py-5">
            <div className="text-danger mb-3">
              <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="text-danger">Erro ao carregar dados</h5>
            <p className="text-muted">{error}</p>
            <button className="btn btn-primary" onClick={fetchData}>
              Tentar novamente
            </button>
          </CardBody>
        </Card>
      );
    }

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <div className="d-flex align-items-center justify-content-between">
            <h5 className={styles.cardTitle}>Relatórios de Entrega</h5>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="card-header align-items-center d-flex">
            <div className="flex-shrink-0 ms-2">
              <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0 nav-tabs">
                <NavItem>
                  <NavLink
                    style={{ cursor: 'pointer' }}
                    onClick={() => setReportActiveTab('1')}
                    className={classnames('nav-link', { active: reportActiveTab === '1' })}
                  >
                    Entrega Geral
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{ cursor: 'pointer' }}
                    onClick={() => setReportActiveTab('2')}
                    className={classnames('nav-link', { active: reportActiveTab === '2' })}
                  >
                    Entrega por dia
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
            {/* Column selector menu */}
            {reportActiveTab === '1' && (
              <div className="ms-auto">
                <Dropdown isOpen={columnByPublisherDropdownOpen} toggle={toggleColumnByPublisherDropdown}>
                  <DropdownToggle color="secondary" size="sm">
                    Selecionar colunas
                  </DropdownToggle>
                  <DropdownMenu style={{ zIndex: 9999, position: 'absolute' }}>
                    {columnsByPublisher
                      .map((col, idx) => {
                        // Filter out media interaction columns if no audio/video creatives
                        const mediaInteractionColumns = [
                          'midia_start', 'midia_25', 'midia_50', 'midia_75', 'midia_complete',
                          'midia_skip', 'midia_mute', 'midia_unmute', 'midia_pause', 'midia_rewind',
                          'midia_resume', 'midia_fullscreen', 'midia_exit_fullscreen', 'midia_close',
                          'midia_expand', 'midia_collapse', 'midia_accept_invitation', 'midia_other_interaction',
                        ];
                        
                        if (mediaInteractionColumns.includes(col.id) && !hasAudioOrVideoCreatives()) {
                          return null;
                        }
                        return { col, idx };
                      })
                      .filter((item): item is { col: any; idx: number } => item !== null)
                      .map(({ col, idx }) => (
                        <DropdownItem key={idx} toggle={false} className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            checked={visibleColumnsByPublisher.includes(idx)}
                            onChange={() => handleColumnByPublisherToggle(idx)}
                            style={{ marginRight: '8px' }}
                          />
                          {col.header}
                        </DropdownItem>
                      ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
            {reportActiveTab === '2' && (
              <div className="ms-auto">
                <Dropdown isOpen={columnByDayDropdownOpen} toggle={toggleColumnByDayDropdown}>
                  <DropdownToggle color="secondary" size="sm">
                    Selecionar colunas
                  </DropdownToggle>
                  <DropdownMenu style={{ zIndex: 9999, position: 'absolute' }}>
                    {columnsByDay
                      .map((col, idx) => {
                        // Filter out media interaction columns if no audio/video creatives
                        const mediaInteractionColumns = [
                          'midia_start', 'midia_25', 'midia_50', 'midia_75', 'midia_complete',
                          'midia_skip', 'midia_mute', 'midia_unmute', 'midia_pause', 'midia_rewind',
                          'midia_resume', 'midia_fullscreen', 'midia_exit_fullscreen', 'midia_close',
                          'midia_expand', 'midia_collapse', 'midia_accept_invitation', 'midia_other_interaction',
                        ];
                        
                        if (mediaInteractionColumns.includes(col.id) && !hasAudioOrVideoCreatives()) {
                          return null;
                        }
                        return { col, idx };
                      })
                      .filter((item): item is { col: any; idx: number } => item !== null)
                      .map(({ col, idx }) => (
                        <DropdownItem key={idx} toggle={false} className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            checked={visibleColumnsByDay.includes(idx)}
                            onChange={() => handleColumnByDayToggle(idx)}
                            style={{ marginRight: '8px' }}
                          />
                          {col.header}
                        </DropdownItem>
                      ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            )}
          </div>

          <div className="card-body">
            <div className="tab-content text-muted">
              <div id="entrega-geral" className={classnames('tab-pane', { active: reportActiveTab === '1' })}>
                {adReport ? (
                  <TableContainer
                    divClass="table-responsive"
                    columns={columnsByPublisher.filter((col, idx) => {
                      const mediaInteractionColumns = [
                        'midia_start', 'midia_25', 'midia_50', 'midia_75', 'midia_complete',
                        'midia_skip', 'midia_mute', 'midia_unmute', 'midia_pause', 'midia_rewind',
                        'midia_resume', 'midia_fullscreen', 'midia_exit_fullscreen', 'midia_close',
                        'midia_expand', 'midia_collapse', 'midia_accept_invitation', 'midia_other_interaction',
                      ];
                      
                      if (mediaInteractionColumns.includes(col.id) && !hasAudioOrVideoCreatives()) {
                        return false;
                      }
                      return visibleColumnsByPublisher.includes(idx);
                    })}
                    data={adReport}
                    isGlobalFilter={false}
                    customPageSize={30}
                    initialSorting={[{ id: 'remaining', desc: true }]}
                  />
                ) : (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                )}
              </div>
              <div id="entrega-por-dia" className={classnames('tab-pane', { active: reportActiveTab === '2' })}>
                {adReportByDay ? (
                  <TableContainer
                    divClass="table-responsive"
                    columns={columnsByDay.filter((col, idx) => {
                      const mediaInteractionColumns = [
                        'midia_start', 'midia_25', 'midia_50', 'midia_75', 'midia_complete',
                        'midia_skip', 'midia_mute', 'midia_unmute', 'midia_pause', 'midia_rewind',
                        'midia_resume', 'midia_fullscreen', 'midia_exit_fullscreen', 'midia_close',
                        'midia_expand', 'midia_collapse', 'midia_accept_invitation', 'midia_other_interaction',
                      ];
                      
                      if (mediaInteractionColumns.includes(col.id) && !hasAudioOrVideoCreatives()) {
                        return false;
                      }
                      return visibleColumnsByDay.includes(idx);
                    })}
                    data={adReportByDay}
                    isGlobalFilter={false}
                    customPageSize={30}
                    initialSorting={[{ id: 'date', desc: true }]}
                  />
                ) : (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
);

DeliveryReportTabs.displayName = 'DeliveryReportTabs';

export default DeliveryReportTabs;
